import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AuthenticationResult,
  type Configuration
} from '@azure/msal-browser';

function env(key: keyof ImportMetaEnv): string {
  return (import.meta.env[key] ?? '').trim();
}

/** True when Vite exposes client id, authority, and API scope (procureflo-ui → procureflo-api). */
export function isEntraLoginAvailable(): boolean {
  return Boolean(env('VITE_MSAL_CLIENT_ID') && env('VITE_MSAL_AUTHORITY') && env('VITE_MSAL_API_SCOPE'));
}

let msalSingleton: Promise<PublicClientApplication> | null = null;

function msalConfiguration(): Configuration {
  const clientId = env('VITE_MSAL_CLIENT_ID');
  const authority = env('VITE_MSAL_AUTHORITY');
  const redirectUri = env('VITE_MSAL_REDIRECT_URI') || `${window.location.protocol}//${window.location.host}`;
  return {
    auth: {
      clientId,
      authority,
      redirectUri
    },
    cache: {
      cacheLocation: 'localStorage'
    }
  };
}

/** Shared instance; callers must await before interactive login at startup via bootstrap. */
export async function getEntraMsal(): Promise<PublicClientApplication> {
  if (!isEntraLoginAvailable()) {
    throw new Error(
      'Set VITE_MSAL_CLIENT_ID, VITE_MSAL_AUTHORITY, and VITE_MSAL_API_SCOPE (.env.local) for Entra sign-in.'
    );
  }
  if (!msalSingleton) {
    msalSingleton = (async (): Promise<PublicClientApplication> => {
      const pca = new PublicClientApplication(msalConfiguration());
      await pca.initialize();
      return pca;
    })();
  }
  return msalSingleton;
}

function delegatedApiScope(): string {
  const s = env('VITE_MSAL_API_SCOPE');
  if (!s) {
    throw new Error('VITE_MSAL_API_SCOPE is required (full scope URI, e.g. api://<api-client-id>/access_as_user).');
  }
  return s;
}

/**
 * Returns an access token for the Procurement API (custom scope).
 * Uses silent refresh when possible; otherwise popup login or consent.
 */
export async function acquireEntraApiAccessToken(): Promise<AuthenticationResult> {
  const pca = await getEntraMsal();
  const scopes = [delegatedApiScope()];
  const active = pca.getActiveAccount();
  const accounts = pca.getAllAccounts();
  const account = active ?? (accounts.length === 1 ? accounts[0] : undefined);

  if (account) {
    try {
      const silent = await pca.acquireTokenSilent({ scopes, account });
      pca.setActiveAccount(silent.account);
      return silent;
    } catch (e) {
      if (!(e instanceof InteractionRequiredAuthError)) {
        throw e;
      }
    }
  }

  const popup = await pca.loginPopup({ scopes });
  if (!popup.account) {
    throw new Error('Microsoft sign-in did not return an account.');
  }
  pca.setActiveAccount(popup.account);
  if (popup.accessToken) {
    return popup;
  }
  return pca.acquireTokenPopup({ scopes, account: popup.account });
}

/** Clears MSAL cached account (best effort). Call when logging out after an Entra session. */
export async function signOutEntraPopup(): Promise<void> {
  if (!isEntraLoginAvailable()) {
    return;
  }
  try {
    const pca = await getEntraMsal();
    const account = pca.getActiveAccount() ?? pca.getAllAccounts()[0];
    if (account) {
      await pca.logoutPopup({ account });
    }
  } catch {
    /* ignore */
  }
}
